/**
 * Сервис валидации и безопасности для синхронизации полей
 * Обеспечивает проверки доступности, валидацию и предотвращение конфликтов
 */

import { getCurrentAmoFieldValue, getAllAmoFields } from '../utils/amoFieldsUtils';
import { getFieldMapping } from './fieldMatchingService';


window.SYNC_DEBUG_SKIP_VISIBILITY_CHECK = true;

/**
 * Проверка доступности AMO поля для записи
 * @param {string|number} fieldId - ID AMO поля
 * @returns {Object} Результат проверки {available: boolean, reason?: string}
 */
export function isAmoFieldWritable(fieldId) {
    try {
        if (!AMOCRM.data.current_card?.model?.attributes) {
            return { available: false, reason: 'Нет доступа к атрибутам карточки' };
        }
        
        
        const possibleSelectors = [
            `[data-id="${fieldId}"]`,
            `[name="CFV[${fieldId}]"]`,
            `#cf_${fieldId}`,
            `[data-field-id="${fieldId}"]`,
            `input[data-id="${fieldId}"]`,
            `select[data-id="${fieldId}"]`,
            `textarea[data-id="${fieldId}"]`
        ];
        
        let fieldElement = null;
        for (const selector of possibleSelectors) {
            fieldElement = document.querySelector(selector);
            if (fieldElement) break;
        }
        
        
        if (!fieldElement) {
            console.warn(`AMO поле ${fieldId} не найдено в DOM, но будет попытка синхронизации`);
            return { available: true }; 
        }
        
        
        if (window.SYNC_DEBUG_SKIP_VISIBILITY_CHECK) {
            console.warn(`ОТЛАДКА: Пропускаем проверки для поля ${fieldId}`);
            return { available: true };
        }
        
        
        if (fieldElement.disabled || fieldElement.readOnly) {
            return { available: false, reason: 'Поле заблокировано для редактирования' };
        }
        
        
        const computedStyle = getComputedStyle(fieldElement);
        if (fieldElement.style.display === 'none' || 
            computedStyle.display === 'none' ||
            computedStyle.visibility === 'hidden') {
            
            let parent = fieldElement.parentElement;
            let isHiddenByParent = false;
            while (parent && parent !== document.body) {
                const parentStyle = getComputedStyle(parent);
                if (parentStyle.display === 'none' || parentStyle.visibility === 'hidden') {
                    isHiddenByParent = true;
                    break;
                }
                parent = parent.parentElement;
            }
            
            if (isHiddenByParent) {
                return { available: false, reason: 'Поле скрыто родительским элементом' };
            }
            
            
            console.warn(`AMO поле ${fieldId} скрыто, но разрешаем синхронизацию`);
            return { available: true };
        }
        
        return { available: true };
    } catch (error) {
        console.error('Ошибка проверки доступности поля:', error);
        
        return { available: true };
    }
}

/**
 * Валидация значения перед синхронизацией
 * @param {*} value - Значение для валидации
 * @param {Object} customField - Кастомное поле
 * @param {Object} amoField - AMO поле
 * @returns {Object} Результат валидации {valid: boolean, reason?: string}
 */
export function validateSyncValue(value, customField, amoField) {
    try {
        
        
        if (value === null || value === undefined) {
            return { valid: true };
        }
        
        
        if (customField.type === 'S') {
            if (typeof value !== 'string') {
                return { valid: false, reason: 'Значение селектора должно быть строкой' };
            }
            
            
            if (value.trim() === '') {
                return { valid: true };
            }
            
            
            if (value.length > 255) {
                return { valid: false, reason: 'Значение слишком длинное (максимум 255 символов)' };
            }
        }
        
        
        if (customField.type === 'M') {
            if (!Array.isArray(value)) {
                return { valid: false, reason: 'Значение мультисписка должно быть массивом' };
            }
            
            
            if (value.length === 0) {
                return { valid: true }; 
            }
            
            
            for (const item of value) {
                if (typeof item !== 'string' || item.trim() === '') {
                    return { valid: false, reason: 'Все значения мультисписка должны быть непустыми строками' };
                }
                
                if (item.length > 255) {
                    return { valid: false, reason: 'Значение слишком длинное (максимум 255 символов)' };
                }
            }
            
            
            if (value.length > 50) {
                return { valid: false, reason: 'Слишком много значений в мультисписке (максимум 50)' };
            }
        }
        
        
        if (amoField) {
            if (amoField.type === 'SELECT' && customField.type === 'S') {
                
                const normalizedValue = value.toLowerCase().trim();
                const hasMatchingEnum = Object.values(amoField.enums || {}).some(enumData => {
                    let enumValue = '';
                    if (typeof enumData === 'string') {
                        enumValue = enumData;
                    } else if (enumData && typeof enumData === 'object') {
                        enumValue = enumData.VALUE || enumData['~VALUE'] || '';
                    }
                    return enumValue && enumValue.toLowerCase().trim() === normalizedValue;
                });
                
                if (!hasMatchingEnum) {
                    return { valid: true, warning: `Значение "${value}" не найдено в AMO селекторе` };
                }
            }
            
            if (amoField.type === 'MULTISELECT' && customField.type === 'M') {
                
                const notFound = [];
                value.forEach(val => {
                    const normalized = val.toLowerCase().trim();
                    const hasMatch = Object.values(amoField.enums || {}).some(enumData => {
                        let enumValue = '';
                        if (typeof enumData === 'string') {
                            enumValue = enumData;
                        } else if (enumData && typeof enumData === 'object') {
                            enumValue = enumData.VALUE || enumData['~VALUE'] || '';
                        }
                        return enumValue && enumValue.toLowerCase().trim() === normalized;
                    });
                    
                    if (!hasMatch) {
                        notFound.push(val);
                    }
                });
                
                if (notFound.length > 0) {
                    return { 
                        valid: true, 
                        warning: `Значения не найдены в AMO мультисписке: ${notFound.join(', ')}` 
                    };
                }
            }
        }
        
        return { valid: true };
    } catch (error) {
        console.error('Ошибка валидации значения:', error);
        return { valid: false, reason: 'Ошибка валидации' };
    }
}

/**
 * Проверка точного совпадения значения кастомного поля с AMO enum
 * @param {string} customValue - Значение кастомного поля
 * @param {Object} amoEnums - Объект enum значений AMO поля
 * @returns {boolean} Есть ли точное совпадение
 */
function hasExactValueMatch(customValue, amoEnums) {
    if (!customValue || !amoEnums) return false;
    
    const normalizedCustom = customValue.toLowerCase().trim();
    
    return Object.values(amoEnums).some(enumData => {
        let enumValue = '';
        if (typeof enumData === 'string') {
            enumValue = enumData;
        } else if (enumData && typeof enumData === 'object') {
            enumValue = enumData.VALUE || enumData['~VALUE'] || '';
        }
        
        return enumValue && enumValue.toLowerCase().trim() === normalizedCustom;
    });
}

/**
 * Проверка конфликтов синхронизации с учетом автоматической замены при точных совпадениях
 * @param {Object} customField - Кастомное поле
 * @param {*} newValue - Новое значение
 * @returns {Object} Результат проверки {hasConflict: boolean, conflict?: Object, canAutoReplace?: boolean}
 */
export function checkSyncConflicts(customField, newValue) {
    return { hasConflict: false, canAutoReplace: true };
}

/**
 * Безопасная синхронизация с предварительными проверками
 * @param {Object} customField - Кастомное поле
 * @param {*} value - Значение для синхронизации
 * @param {Object} options - Опции синхронизации
 * @returns {Object} Результат проверки безопасности
 */
export function validateSafeSync(customField, value, options = {}) {
    const { allowOverwrite = false, skipConflictCheck = false } = options;
    
    try {
        
        const mapping = getFieldMapping(customField);
        if (!mapping) {
            return { 
                safe: false, 
                reason: 'Нет сопоставления с AMO полем',
                canProceed: false
            };
        }
        
        
        const accessibility = isAmoFieldWritable(mapping.amoField.id);
        if (!accessibility.available) {
            return { 
                safe: false, 
                reason: accessibility.reason,
                canProceed: false
            };
        }
        
        
        const validation = validateSyncValue(value, customField, mapping.amoField);
        if (!validation.valid) {
            return { 
                safe: false, 
                reason: validation.reason,
                canProceed: false
            };
        }
        
        
        if (!skipConflictCheck) {
            const conflicts = checkSyncConflicts(customField, value);
            if (conflicts.hasConflict && !allowOverwrite) {
                return { 
                    safe: false, 
                    reason: 'Конфликт значений с AMO полем',
                    conflict: conflicts.conflict,
                    canProceed: false,
                    requiresOverwrite: true
                };
            }
        }
        
        return { 
            safe: true, 
            mapping,
            warning: validation.warning,
            canProceed: true
        };
    } catch (error) {
        console.error('Ошибка проверки безопасности синхронизации:', error);
        return { 
            safe: false, 
            reason: 'Ошибка проверки безопасности',
            canProceed: false
        };
    }
}

/**
 * Получение рекомендаций по синхронизации для чек-листа
 * @param {Array} customFields - Кастомные поля чек-листа
 * @returns {Object} Рекомендации по синхронизации
 */
export function getSyncRecommendations(customFields) {
    try {
        const recommendations = {
            syncable: [],
            conflicts: [],
            unavailable: [],
            warnings: []
        };
        
        customFields.forEach(field => {
            if (field.type === 'C') return; 
            
            const mapping = getFieldMapping(field);
            if (!mapping) {
                recommendations.unavailable.push({
                    field,
                    reason: 'Нет сопоставления с AMO полем'
                });
                return;
            }
            
            const accessibility = isAmoFieldWritable(mapping.amoField.id);
            if (!accessibility.available) {
                recommendations.unavailable.push({
                    field,
                    reason: accessibility.reason
                });
                return;
            }
            
            const currentValue = field.content?.value || field.content?.values;
            if (!currentValue || 
                (Array.isArray(currentValue) && currentValue.length === 0) ||
                (typeof currentValue === 'string' && currentValue.trim() === '')) {
                return; 
            }
            
            const conflicts = checkSyncConflicts(field, currentValue);
            if (conflicts.hasConflict) {
                recommendations.conflicts.push({
                    field,
                    conflict: conflicts.conflict
                });
            } else {
                recommendations.syncable.push({
                    field,
                    mapping
                });
            }
        });
        
        return recommendations;
    } catch (error) {
        console.error('Ошибка получения рекомендаций:', error);
        return {
            syncable: [],
            conflicts: [],
            unavailable: [],
            warnings: []
        };
    }
} 